/**
 * Wiki Display Fields Configuration
 *
 * Controls which characteristics are shown on item cards based on source hierarchy.
 * Works similar to wikiFiltersConfig - traverses sourceChain and merges configs.
 *
 * FULL CONTROL: If source is not in config, default fields are shown.
 */

export type DisplayField =
  | 'level'
  | 'crystalType'
  | 'patk'
  | 'matk'
  | 'patkSpd'
  | 'weaponType'
  | 'pdef'
  | 'mdef'
  | 'maxHp'
  | 'maxMp'
  | 'armorType'
  | 'qualityType';

export interface DisplayConfig {
  fields?: DisplayField[];
  excludeFields?: DisplayField[];
}

/**
 * Configuration by source
 * Key = source value from hierarchy (matched during bottom-up traversal)
 *
 * NOTE: Keys must match source values from wikiFiltersConfig.ts
 *
 * HIERARCHY:
 * items (base) → weapon, equipment, accessories, bijouterie
 *   weapon → magical (excludes physical attack)
 *
 * Fields like pdef/mdef won't show for weapons because item.pdef will be 0/undefined
 */
export const wikiDisplayConfig: Record<string, DisplayConfig> = {
  // === ITEMS (base) ===
  // All possible fields - children inherit, values determine visibility
  items: {
    fields: [
      'level', 'crystalType', 'qualityType',
      'pdef', 'mdef', 'maxHp', 'maxMp', 'armorType',
    ],
  },

  // === WEAPONS ===
  // Adds attack stats
  weapon: {
    fields: ['patk', 'patkSpd', 'weaponType'],
  },

  // === MAGICAL WEAPONS ===
  // Removes physical attack
  magical: {
    excludeFields: ['patk', 'patkSpd'],
    fields: ['matk']
  },
};

/**
 * Get merged display config for a source chain
 * Traverses from top to bottom, merging configs (deeper overrides/adds)
 *
 * @param sourceChain - Array of sources from hierarchy [category, subCategory, type, subType]
 * @returns DisplayConfig with merged fields
 */
export function getDisplayConfig(sourceChain: (string | undefined | null)[]): DisplayConfig {
  let mergedFields: DisplayField[] = [];
  let hasAnyConfig = false;

  // Traverse from beginning (top) to end (deepest)
  for (const source of sourceChain) {
    if (source && wikiDisplayConfig[source]) {
      hasAnyConfig = true;
      const config = wikiDisplayConfig[source];

      // Apply excludes first (remove from inherited)
      if (config.excludeFields) {
        mergedFields = mergedFields.filter(f => !config.excludeFields!.includes(f));
      }

      // Merge fields (avoid duplicates)
      if (config.fields) {
        for (const field of config.fields) {
          if (!mergedFields.includes(field)) {
            mergedFields.push(field);
          }
        }
      }
    }
  }

  return {
    fields: hasAnyConfig ? mergedFields : undefined,
  };
}

/**
 * Check if a field should be displayed for given source chain
 */
export function shouldDisplayField(
  field: DisplayField,
  sourceChain: (string | undefined | null)[]
): boolean {
  const config = getDisplayConfig(sourceChain);

  // If no config, show all fields (default behavior)
  if (!config.fields) {
    return true;
  }

  return config.fields.includes(field);
}
